export interface MediaRatio { numerator: number; denominator: number }
export interface MediaInspectionFilters {
    min_duration_ms: number | null; max_duration_ms: number | null;
    min_width: number | null; min_height: number | null; aspect_ratios: MediaRatio[];
}
export type MarketMediaInspectionConfig = { schema_version: 'market-media-inspection-config/v1'; enabled: false }
    | { schema_version: 'market-media-inspection-config/v1'; enabled: true; max_records: number; max_assets: number;
        max_source_bytes: number; filters: MediaInspectionFilters };

export const mediaInteger = (value: unknown, min: number, max: number): value is number =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
export const mediaObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
export function mediaGcd(a: number, b: number): number { while (b) { const next = a % b; a = b; b = next; } return a; }

/** Mirrors Platform's exact native-inspection contract; no model or collection permission. */
export function normalizeMediaInspectionConfig(value: unknown): MarketMediaInspectionConfig {
    const invalid = () => new Error('Invalid media inspection configuration');
    if (!mediaObject(value) || value.schema_version !== 'market-media-inspection-config/v1' || typeof value.enabled !== 'boolean') throw invalid();
    if (!value.enabled) {
        if (Object.keys(value).length !== 2) throw invalid();
        return { schema_version: 'market-media-inspection-config/v1', enabled: false };
    }
    const f = value.filters;
    if (Object.keys(value).length !== 6 || !mediaInteger(value.max_records, 1, 100) || !mediaInteger(value.max_assets, 1, 100)
        || !mediaInteger(value.max_source_bytes, 45, 2_000_000_000) || Math.floor(value.max_source_bytes / value.max_assets) < 45
        || !mediaObject(f) || Object.keys(f).length !== 5
        || !['min_duration_ms', 'max_duration_ms'].every(key => f[key] === null || mediaInteger(f[key], 0, 600_000))
        || !['min_width', 'min_height'].every(key => f[key] === null || mediaInteger(f[key], 1, 8192))
        || (f.min_duration_ms !== null && f.max_duration_ms !== null && Number(f.min_duration_ms) > Number(f.max_duration_ms))
        || !Array.isArray(f.aspect_ratios) || f.aspect_ratios.length > 10) throw invalid();
    const ratios = new Map<string, MediaRatio>();
    for (const ratio of f.aspect_ratios) {
        if (!mediaObject(ratio) || Object.keys(ratio).length !== 2 || !mediaInteger(ratio.numerator, 1, 8192) || !mediaInteger(ratio.denominator, 1, 8192)) throw invalid();
        const gcd = mediaGcd(ratio.numerator, ratio.denominator);
        const reduced = { numerator: ratio.numerator / gcd, denominator: ratio.denominator / gcd };
        const key = `${reduced.numerator}:${reduced.denominator}`;
        if (ratios.has(key)) throw invalid();
        ratios.set(key, reduced);
    }
    return { schema_version: 'market-media-inspection-config/v1', enabled: true, max_records: value.max_records,
        max_assets: value.max_assets, max_source_bytes: value.max_source_bytes, filters: {
            min_duration_ms: f.min_duration_ms as number | null, max_duration_ms: f.max_duration_ms as number | null,
            min_width: f.min_width as number | null, min_height: f.min_height as number | null,
            // Java TreeMap lexical order, not locale-dependent or numeric order.
            aspect_ratios: [...ratios.keys()].sort().map(key => ratios.get(key)!),
        } };
}
export function validMediaInspectionConfig(value: unknown): boolean {
    if (value === undefined) return true;
    try { normalizeMediaInspectionConfig(value); return true; } catch { return false; }
}
export function mediaInspectionConfigMatches(requested: unknown, echoed: unknown): boolean {
    if (requested === undefined) return true;
    try { return JSON.stringify(normalizeMediaInspectionConfig(requested)) === JSON.stringify(normalizeMediaInspectionConfig(echoed)); }
    catch { return false; }
}
